import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getOrganizationSuggestions,
  OrganizationSuggestion,
} from "../../../services/searchApi";
import DeletableChip from "./DeletableChip";

// Custom debounce implementation to avoid lodash dependency
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) & { cancel: () => void } => {
  let timeout: NodeJS.Timeout;
  const debounced = (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
  debounced.cancel = () => clearTimeout(timeout);
  return debounced as any;
};

export interface CompanyOption {
  id: string;
  name: string;
  country?: string;
  country_iso2?: string;
  display_label?: string;
}

interface VirtualizedCompanyDropdownProps {
  label: string;
  value: CompanyOption[];
  onChange: (value: CompanyOption[]) => void;
  placeholder?: string;
  multiple?: boolean;
}

const VirtualizedCompanyDropdown: React.FC<VirtualizedCompanyDropdownProps> = ({
  label,
  value,
  onChange,
  placeholder = "Search for companies...",
  multiple = true,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<OrganizationSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  // Debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce(async (searchTerm: string) => {
        if (!searchTerm || searchTerm.length < 2) {
          setOptions([]);
          setLoading(false);
          return;
        }

        try {
          setLoading(true);

          const response = await getOrganizationSuggestions(searchTerm, 20);
          setOptions(response.suggestions || []);
        } catch (error) {
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, 300),
    []
  );

  // Effect to trigger search when input changes
  useEffect(() => {
    if (open) {
      debouncedSearch(inputValue);
    }

    return () => {
      debouncedSearch.cancel();
    };
  }, [inputValue, open, debouncedSearch]);

  // Handle selection change
  const handleChange = useCallback(
    (event: any, newValue: CompanyOption | CompanyOption[] | null) => {
      if (multiple) {
        onChange((newValue as CompanyOption[]) || []);
      } else {
        onChange(newValue ? [newValue as CompanyOption] : []);
      }
    },
    [onChange, multiple]
  );

  // Custom option rendering with country info
  const renderOption = (props: any, option: CompanyOption) => (
    <Box component="li" {...props} key={option.id}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: "medium" }}>
          {option.name}
        </Typography>
        {option.country_iso2 && (
          <Chip
            label={option.country_iso2}
            size="small"
            variant="outlined"
            sx={{
              height: 20,
              fontSize: "0.75rem",
              "& .MuiChip-label": {
                px: 1,
              },
            }}
          />
        )}
      </Box>
    </Box>
  );

  // Custom tag rendering for selected companies
  const renderTags = (tagValue: CompanyOption[], getTagProps: any) =>
    tagValue.map((option, index) => {
      const tagProps = getTagProps({ index });
      return (
        <DeletableChip
          key={option.id}
          label={option.name}
          onDelete={tagProps.onDelete}
          maxChars={25}
        />
      );
    });

  return (
    <Autocomplete
      multiple={multiple}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={multiple ? value : value[0] || null}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
      options={options}
      getOptionLabel={(option) => {
        const label = option.display_label || option.name || "";
        // Truncate for single-select mode when displayed in input field
        if (!multiple && label.length > 20) {
          return label.substring(0, 20) + "...";
        }
        return label;
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      loading={loading}
      loadingText="Searching companies..."
      noOptionsText={
        inputValue.length < 2
          ? "Type at least 2 characters to search"
          : "No companies found"
      }
      renderOption={renderOption}
      renderTags={multiple ? renderTags : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{
        "& .MuiAutocomplete-inputRoot": {
          paddingRight: "9px !important",
        },
        "& .MuiAutocomplete-tag": {
          margin: "3px 4px 3px 0",
        },
      }}
    />
  );
};

export default VirtualizedCompanyDropdown;
